import {
  STREAM_EDIT_FIELDS,
  type StreamEditField,
  type HitDetail,
  type StreamEditValues,
  type StreamHit,
  type StreamHitsResponse,
} from '@headline/schemas'

/**
 * Editing the Library's articles, on the browser's side (CLI-1021). An edit is stored on the server
 * as a hit override and reaches the cached rows only when the analytics backend next builds the
 * workspace (hourly). Until then this browser keeps the edit and lays it over every read, so the
 * person who made it sees it at once; everyone else sees it after the build.
 *
 * An edit is kept until a read says its cache was built after the edit (`computedAt`), and never
 * longer than `PENDING_EDIT_MAX_MS` — a build that keeps failing must not leave this browser
 * showing a value nobody else will ever see. A reset is not laid over anything: the browser does
 * not know the build's own value, so the row shows what the cache has until it is rebuilt.
 *
 * localStorage, per workspace, so the edits outlive a reload and reach every tab; blocked storage
 * degrades to "the edit shows after the build", never to a crash.
 */

const PREFIX = 'headline.stream-edits.v1:'
/** The longest a browser shows an edit the cache does not have yet. */
export const PENDING_EDIT_MAX_MS = 6 * 60 * 60 * 1000

/** One article's edits this browser made since the cache was last built. */
interface PendingEdit {
  /** When the server stored the latest of them (its clock, from the edit's response). */
  at: string
  set: StreamEditValues
  clear: StreamEditField[]
}
/** By hit id. */
export type PendingEdits = Record<string, PendingEdit>

/** An edit as the route takes it: values to set, fields to put back. */
export interface StreamEdit {
  set: StreamEditValues
  clear: StreamEditField[]
}

/** The pending edits with a later edit of these hits merged in: its values win, its resets drop
 *  what was set before. */
export function mergePending(
  pending: PendingEdits,
  hitIds: readonly string[],
  edit: StreamEdit,
  at: string,
): PendingEdits {
  const next = { ...pending }
  for (const hitId of hitIds) {
    const prev = next[hitId]
    const set: StreamEditValues = { ...prev?.set }
    for (const field of edit.clear) delete set[field]
    const kept = (prev?.clear ?? []).filter((field) => edit.set[field] === undefined)
    next[hitId] = {
      at,
      set: { ...set, ...edit.set },
      clear: [...new Set([...kept, ...edit.clear])],
    }
  }
  return next
}

/** The pending edits a read still lacks: not yet built (`computedAt` before the edit), not too old. */
export function livePending(
  pending: PendingEdits,
  computedAt: string | null,
  now: number,
): PendingEdits {
  const built = computedAt ? Date.parse(computedAt) : Number.NEGATIVE_INFINITY
  return Object.fromEntries(
    Object.entries(pending).filter(([, edit]) => {
      const at = Date.parse(edit.at)
      return Number.isFinite(at) && at >= built && now - at <= PENDING_EDIT_MAX_MS
    }),
  )
}

/** The row with an edit's values in place of its own. The edit fields are the row's own names. */
function editedHit(hit: StreamHit, values: StreamEditValues): StreamHit {
  const next = { ...hit }
  for (const field of STREAM_EDIT_FIELDS) {
    const value = values[field]
    if (value !== undefined) Object.assign(next, { [field]: value })
  }
  return next
}

/** The read with the edits laid over its rows; the same object when none of them is in it. */
export function withEdits(read: StreamHitsResponse, pending: PendingEdits): StreamHitsResponse {
  let changed = false
  const hits = read.hits.map((hit) => {
    const edit = pending[hit.id]
    if (!edit || Object.keys(edit.set).length === 0) return hit
    changed = true
    return editedHit(hit, edit.set)
  })
  return changed ? { ...read, hits } : read
}

function readStored(subaccountId: string): PendingEdits {
  try {
    const raw = window.localStorage.getItem(PREFIX + subaccountId)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? (parsed as PendingEdits) : {}
  } catch {
    return {}
  }
}

function writeStored(subaccountId: string, pending: PendingEdits) {
  try {
    if (Object.keys(pending).length === 0) window.localStorage.removeItem(PREFIX + subaccountId)
    else window.localStorage.setItem(PREFIX + subaccountId, JSON.stringify(pending))
  } catch {
    // Blocked or full storage: the edit shows after the build instead.
  }
}

/** A fresh read of the workspace's rows with this browser's unbuilt edits laid over it — and the
 *  edits the read already has forgotten. */
export function applyPendingEdits(
  subaccountId: string,
  read: StreamHitsResponse,
  now = Date.now(),
): StreamHitsResponse {
  const stored = readStored(subaccountId)
  if (Object.keys(stored).length === 0) return read
  const live = livePending(stored, read.computedAt, now)
  if (Object.keys(live).length !== Object.keys(stored).length) writeStored(subaccountId, live)
  return withEdits(read, live)
}

/** Keep an edit the server just stored; answers the pending edits of those hits, to lay over the
 *  reads already loaded. */
export function recordPendingEdit(
  subaccountId: string,
  hitIds: readonly string[],
  edit: StreamEdit,
  at: string,
): PendingEdits {
  const merged = mergePending(
    livePending(readStored(subaccountId), null, Date.parse(at)),
    hitIds,
    edit,
    at,
  )
  writeStored(subaccountId, merged)
  return Object.fromEntries(hitIds.flatMap((id) => (merged[id] ? [[id, merged[id]]] : [])))
}

/* ── The edit dialog's form ─────────────────────────────────────────────────────────────── */

/** One field in the dialog: left as it is, given a value, or put back to the build's own. */
export interface FieldDraft {
  mode: 'keep' | 'set' | 'reset'
  value: string
}
export type EditDraft = Record<StreamEditField, FieldDraft>

const valueText = (hit: StreamHit, field: StreamEditField): string => {
  const value = hit[field]
  return value === null || value === undefined ? '' : String(value)
}

/** Whether a row carries a value for the field: a reach above zero, any other field non-empty. */
const carries = (hit: StreamHit, field: StreamEditField): boolean => {
  const value = hit[field]
  return field === 'reach' ? typeof value === 'number' && value > 0 : Boolean(value)
}

/**
 * The fields a workspace's articles can be edited on: those its stream carries — at least one
 * loaded row has a value — so a client whose build scores no sentiment, writes no classification
 * or shows no reach is never offered one. The headline is every row's, so it is always offered.
 */
export function editableFields(corpus: readonly StreamHit[]): StreamEditField[] {
  return STREAM_EDIT_FIELDS.filter(
    (field) => field === 'headline' || corpus.some((hit) => carries(hit, field)),
  )
}

/** The selection's shared value for a field, or null where the articles differ ("Mixed"). */
export function commonValue(hits: readonly StreamHit[], field: StreamEditField): string | null {
  const values = new Set(hits.map((hit) => valueText(hit, field)))
  return values.size === 1 ? [...values][0]! : null
}

/** Every field left as it is, showing the selection's shared value where there is one. */
export function initialDraft(hits: readonly StreamHit[]): EditDraft {
  return Object.fromEntries(
    STREAM_EDIT_FIELDS.map((field) => [
      field,
      { mode: 'keep', value: commonValue(hits, field) ?? '' },
    ]),
  ) as EditDraft
}

/** A reach typed in, or null when it is not a positive whole number. */
function reachOf(text: string): number | null {
  const value = Number(text.replaceAll(',', '').trim())
  return Number.isInteger(value) && value > 0 ? value : null
}

/**
 * The dialog's draft as the edit it sends, plus the fields whose value cannot be sent (empty, or a
 * reach that is not a positive whole number) — the dialog names those and does not save.
 */
export function draftToEdit(draft: EditDraft): StreamEdit & { invalid: StreamEditField[] } {
  const set: Record<string, string | number> = {}
  const clear: StreamEditField[] = []
  const invalid: StreamEditField[] = []
  for (const field of STREAM_EDIT_FIELDS) {
    const { mode, value } = draft[field]
    if (mode === 'reset') clear.push(field)
    if (mode !== 'set') continue
    const parsed = field === 'reach' ? reachOf(value) : value.trim() || null
    if (parsed === null) invalid.push(field)
    else set[field] = parsed
  }
  return { set: set as StreamEditValues, clear, invalid }
}

/**
 * An article as its panel shows it, with its stored edits laid over the cached row and the live
 * record — neither of which carries an edit before the next build, and the live record never does.
 * The live record names three of the fields its own way.
 */
export function withStoredEdits(
  hit: StreamHit,
  detail: HitDetail | undefined,
  values: StreamEditValues | undefined,
): { hit: StreamHit; detail: HitDetail | undefined } {
  if (!values || Object.keys(values).length === 0) return { hit, detail }
  return {
    hit: editedHit(hit, values),
    detail: detail && {
      ...detail,
      title: values.headline ?? detail.title,
      outlet: values.outlet ?? detail.outlet,
      author: values.author ?? detail.author,
    },
  }
}
